import { signInWithEmailAndPassword } from "firebase/auth"
import { httpsCallable } from "firebase/functions"
import { Eye, EyeOff } from "lucide-react"
import { FC, useState } from "react"
import { useAuth, useFunctions } from "../hooks/use-firebase"
import { Button, Input } from "../components/ui"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

export const Login: FC = ({ }) => {

  const auth = useAuth()
  const functions = useFunctions()
  const navigate = useNavigate()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [visible, setVisible] = useState(false)
  const [loginError, setLoginError] = useState("")
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [isResettingPassword, setIsResettingPassword] = useState(false)

  const handleLogin = async () => {
    if (email && password) {
      try {
        await signInWithEmailAndPassword(auth, email, password);
        navigate("/");
      } catch (e: any) {
        console.error('Login failed:', e);

        // Check Firebase error codes for more specific error handling
        if (e.code) {
          switch (e.code) {
            case 'auth/user-not-found':
              setLoginError('No user found with this email.');
              break;
            case 'auth/wrong-password':
              setLoginError('Incorrect password. Please try again.');
              break;
            case 'auth/invalid-email':
              setLoginError('The email address is not valid.');
              break;
            default:
              setLoginError('An unexpected error occurred. Please try again later.');
          }
        } else {
          // Fallback for errors without a code
          setLoginError(e.message || 'Unknown error occurred');
        }
      }
    } else {
      setLoginError('Please provide both email and password.');
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setIsResettingPassword(true);
    setLoginError("");

    try {
      const sendPasswordResetEmail = httpsCallable(functions, 'sendPasswordResetEmail');
      await sendPasswordResetEmail({ email });
      toast.success("Password reset email sent! Check your inbox.");
      setShowForgotPassword(false);
      setEmail("");
    } catch (e: any) {
      console.error('Password reset failed:', e);

      // Handle Cloud Function errors
      const errorCode = e.code;
      const errorMessage = e.message;

      if (errorCode === 'functions/resource-exhausted') {
        toast.error('Too many attempts. Please try again later.');
      } else if (errorCode === 'functions/invalid-argument') {
        toast.error('The email address is not valid.');
      } else {
        toast.error(errorMessage || 'Failed to send reset email. Please try again.');
      }
    } finally {
      setIsResettingPassword(false);
    }
  };


  return (
    <div className="w-screen h-screen flex flex-col md:flex-row">
      <div className="w-none md:w-1/2 flex justify-center items-center">
        <div
          className="font-extrabold text-[30px] text-center text-black dark:text-white md:text-[60px] cursor-pointer"
          onClick={() => navigate("/")}
        >
          INVENTORY<span className="text-sumoPrimary">SUMO</span>
        </div>

      </div>
      <div className="w-full h-full md:w-1/2 bg-black flex justify-center items-center md:items-center">
        <div className="w-[392px] flex flex-col gap-4 p-6 bg-black text-white rounded-sm shadow">
          <div className="text-white text-2xl">
            {showForgotPassword ? "Reset Password" : "Welcome Back"}
          </div>
          <div className="w-full flex flex-col gap-2">
            <Input
              name="email"
              className="bg-white text-black h-12"
              placeholder="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value.trim()) }}
            />

            {!showForgotPassword && (
              <div className="text-white flex items-center w-full">
                <Input
                  name="password"
                  className="bg-white text-black h-12"
                  placeholder="password"
                  type={visible ? "text" : "password"}
                  onChange={(e) => { setPassword(e.target.value.trim()) }}
                />
                <div className="-ml-8" onClick={() => { setVisible(!visible) }}>
                  {visible ? <Eye width={18} /> : <EyeOff width={18} />}
                </div>
              </div>
            )}

            {loginError &&
              <div className="my-2 p-2 bg-error100 border border-2 border-error700 rounded text-error700">
                {loginError}
              </div>}

          </div>

          <div className="w-full flex flex-col gap-2">
            {showForgotPassword ? (
              <>
                <Button
                  variant="primary"
                  className="w-full"
                  disabled={!email || isResettingPassword}
                  onClick={handlePasswordReset}
                >
                  {isResettingPassword ? "Sending..." : "Send Reset Email"}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-white hover:text-sumoPrimary"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setLoginError("");
                  }}
                >
                  Back to Login
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="primary"
                  className="w-full"
                  disabled={!email || !password}
                  onClick={handleLogin}
                >
                  Login
                </Button>

                <Button
                  variant="ghost"
                  className="w-full text-white hover:text-sumoPrimary"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setLoginError("");
                  }}
                >
                  Forgot Password?
                </Button>

                <Button
                  variant="ghost"
                  className="w-full text-white hover:text-sumoPrimary"
                  onClick={() => navigate("/create-account")}
                >
                  Don't have an account? Sign up
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div >
  )
}

